import React from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
  Avatar,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  LocationOn as LocationIcon,
  Store as StoreIcon,
  Schedule as ScheduleIcon,
} from "@mui/icons-material";

export interface LostItemCardProps {
  id: string;
  name: string;
  status: string;
  found_location: string;
  found_at: string;
  store_name?: string;
  media_urls?: string[];
  onView?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}

const statusColor: Record<string, "primary" | "success" | "info" | "default"> =
  {
    Found: "primary",
    Claimed: "success",
    Returned: "info",
    Disposed: "default",
  };

const LostItemCard: React.FC<LostItemCardProps> = ({
  id,
  name,
  status,
  found_location,
  found_at,
  store_name,
  media_urls,
  onView,
  onEdit,
  onDelete,
}) => {
  // null/undefinedチェックを追加
  const safeMediaUrls = media_urls || [];

  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        boxShadow: 3,
      }}
    >
      {safeMediaUrls.length > 0 ? (
        <CardMedia
          component="img"
          image={safeMediaUrls[0]}
          alt={name}
          sx={{
            height: 160,
            objectFit: "cover",
            cursor: onView ? "pointer" : "default",
          }}
          onClick={onView}
        />
      ) : (
        <Box
          sx={{
            height: 160,
            bgcolor: "grey.100",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: onView ? "pointer" : "default",
          }}
          onClick={onView}
        >
          <Avatar sx={{ width: 64, height: 64, bgcolor: "grey.300" }}>
            <StoreIcon fontSize="large" />
          </Avatar>
        </Box>
      )}
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack spacing={1}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", flexGrow: 1 }}
              noWrap
            >
              {name}
            </Typography>
            <Chip
              label={status}
              color={statusColor[status] || "default"}
              size="small"
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LocationIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary" noWrap>
              {found_location}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ScheduleIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {new Date(found_at).toLocaleString("ja-JP", {
                timeZone: "Asia/Tokyo",
              })}
            </Typography>
          </Box>
          {store_name && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <StoreIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary" noWrap>
                {store_name}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
      <Box
        sx={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 1 }}
      >
        {onView && (
          <Tooltip title="詳細">
            <IconButton size="small" onClick={onView}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onEdit && (
          <Tooltip title="編集">
            <IconButton size="small" onClick={onEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title="削除">
            <IconButton size="small" color="error" onClick={onDelete}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Card>
  );
};

export default LostItemCard;
